import Vue from 'vue';
import VueI18n from 'vue-i18n';
import VueSocketIO from 'vue-socket.io';
import VueTour from 'vue-tour';
import { ApmVuePlugin } from '@elastic/apm-rum-vue';
import { io } from 'socket.io-client';

import '@mdi/font/css/materialdesignicons.css';
import '@/register-service-worker';
import './assets/css/global-styles.css';
import 'vue-tour/dist/vue-tour.css';

import cloneDeep from 'lodash/cloneDeep';
import EnvVariableReader from './utils/env-variable-reader';
import FaviconPlugin from '@/plugins/favicon-notifications';
import RingtoneManager from '@/plugins/ringtone-manager/index';
import FeaturesManager from '@/plugins/features-manager/index';
import router from '@/router';
import store from '@/store';
import vuetify from '@/plugins/vuetify';
import App from '@/components/ecs-app/App';
import EcsSearchInput from '@/components/ecs-search-input/EcsSearchInput';
import EcsCustomInput from '@/components/ecs-custom-input/EcsCustomInput';
import EcsSideBarWrapper from '@/components/ecs-app-side-bar/ecs-side-bar-wrapper/EcsSideBarWrapper';
import EcsSideBarInteractionList from '@/components/ecs-app-side-bar/ecs-side-bar-interaction-list/EcsSideBarInteractionList';
import EcsSideBarTileContent from '@/components/ecs-app-side-bar/ecs-side-bar-tile-content/EcsSideBarTileContent';
import EcsImageHandler from '@/directives/ecs-image-handler/EcsImageHandler';
import EcsUserTimeFormat from '@/filters/ecs-user-time-format/EcsUserTimeFormat';
import EcsWaitingAnimation from '@/components/ecs-waiting-animation/ecs-waiting-animation';

// Register reusable components globally.
Vue.component('EcsSearchInput', EcsSearchInput);
Vue.component('EcsCustomInput', EcsCustomInput);
Vue.component('EcsSideBarWrapper', EcsSideBarWrapper);
Vue.component('EcsSideBarInteractionList', EcsSideBarInteractionList);
Vue.component('EcsSideBarTileContent', EcsSideBarTileContent);
Vue.component('EcsWaitingAnimation', EcsWaitingAnimation);

// Register reusable directives globally.
Vue.directive('EcsImageHandler', EcsImageHandler);

// Register reusable filters globally.
Vue.filter('EcsUserTimeFormat', EcsUserTimeFormat.filterFn);

Vue.use(VueI18n);

Vue.use(FaviconPlugin);

// Optionally enable apm
// If APM is disabled, the consuming client code may need to check before using the service
const apmEnabled = EnvVariableReader.readBoolean(window.ECS_AWC.VUE_APP_ELASTIC_APM_ENABLED, false);
Vue.use(ApmVuePlugin, {
  captureErrors: true,
  config: {
    active: apmEnabled,
    breakdownMetrics: true,
    environment: window.ECS_AWC.VUE_APP_ELASTIC_APM_ENVIRONMENT || 'dev',
    monitorLongtasks: true,
    serverUrl: window.ECS_AWC.VUE_APP_ELASTIC_APM_SERVER_URL || '',
    serviceName: window.ECS_AWC.VUE_APP_ELASTIC_APM_SERVICE_NAME || 'ecs-web-client'
  },
  router
});

// Configure plugin options.
const i18n = new VueI18n({
  locale: 'en',
  fallbackLocale: 'en'
});

// Setup connection to the socket
const socketUrl = window.location.origin; // 'http://localhost:3001';
const socket = io(socketUrl, {
  autoConnect: false,
  transports: ['websocket'],
  upgrade: false,
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 20000,
  reconnectionAttempts: Infinity
});

Vue.use(
  new VueSocketIO({
    debug: process.env.NODE_ENV !== 'production',
    connection: socket,
    vuex: {
      store,
      actionPrefix: 'SOCKET_EVENT_'
    }
  })
);

const ringtoneManager = new RingtoneManager();
Vue.use(ringtoneManager);

const featuresManager = new FeaturesManager();
Vue.use(featuresManager);

Vue.use(VueTour);

// Remove console log that indicates you are in development mode.
Vue.config.productionTip = false;

/* eslint-disable no-new */
new Vue({
  el: '#app',
  i18n,
  store,
  router,
  vuetify,
  components: { App },
  template: '<App/>'
});

// structuredClone is not yet supported on MS Teams.
// Using cloneDeep from lodash as fallback.
if (typeof structuredClone === 'undefined') {
  // eslint-disable-next-line no-global-assign
  window.structuredClone = cloneDeep;
}
